import { MdCheck, MdClose } from "react-icons/md";
import Modal from "../Modal/Modal";
import { ICON_SIZES } from "../../helpers/constants";
import { useTranslation } from "react-i18next";
import TextInput from "../TextInput/TextInput";
import { useMemo, useState } from "react";
import CustomSelect, { ICustomSelectOption } from "../Select/CustomSelect";
import { transformToSelectOptions } from "../../helpers/utils";
import useCurrentUser from "../../hooks/useCurrentUser";
import useMainFilter from "../../hooks/useMainFilter";
import clsx from "clsx";
import CheckBox from "../CheckBox/CheckBox";
import ProgressBar from "../ProgressBar/ProgressBar";
import { EButtonKind } from "../Button/ButtonTypes";

interface IDuplicateTransactionsDialog {
  onClose: () => void;
  isLoading?: boolean;
  onConfirm: (values: any) => void;
  count: number;
}

const DuplicateTransactionsDialog: React.FC<IDuplicateTransactionsDialog> = ({
  onClose,
  isLoading = false,
  onConfirm,
  count,
}) => {
  const { t } = useTranslation();
  const { mainFilter } = useMainFilter();
  const [date, setDate] = useState<string>("");
  const [account, setAccount] = useState<string>("");
  const [isUsingCustomAccount, setIsUsingCustomAccount] =
    useState<boolean>(false);
  const [isUsingCustomDate, setIsUsingCustomDate] = useState<boolean>(false);
  const [keepAttachments, setKeepAttachments] = useState<boolean>(false);
  const [currentFile, setCurrentFile] = useState<number>(4);
  const [isRunning, setIsRunning] = useState<boolean>(false);

  const { allUserAccounts } = useCurrentUser();

  const fieldsAreCompleted = useMemo(() => {
    if (isUsingCustomDate && !date) return false;
    if (isUsingCustomAccount && !account) return false;

    return true;
  }, [isUsingCustomAccount, isUsingCustomDate, account, date]);

  return (
    <Modal
      onEscape={() => {
        if (!isLoading) {
          onClose();
        }
      }}
      className={clsx([
        "md:!max-w-[500px]",
        "w-full",
        "md:!w-[500px]",
        "md:!min-w-[500px]",
      ])}
    >
      <Modal.Header
        title={t("dialogs.duplicate_transactions.title")}
        onClose={() => {
          if (!isLoading) {
            onClose();
          }
        }}
      />
      <Modal.Body>
        <div className={clsx(["grid", "grid-cols-1", "gap-4", "w-full"])}>
          {isRunning ? (
            <>
              <ProgressBar progressFiles={currentFile} totalFiles={10} />
            </>
          ) : (
            <>
              <p className={clsx(["text-sm"])}>
                {t("dialogs.duplicate_transactions.count_text", { count })}
              </p>
              <div className={clsx(["grid", "grid-cols-1", "gap-2"])}>
                <div className={clsx(["flex", "w-full", "flex-col", "gap-1"])}>
                  <CheckBox
                    disabled={isLoading}
                    label="Asignar una fecha personalizada"
                    onChange={(isChecked) => setIsUsingCustomDate(isChecked)}
                    checked={isUsingCustomDate}
                    description={t(
                      "dialogs.duplicate_transactions.custom_date_text",
                      {
                        count,
                      }
                    )}
                  />
                  {isUsingCustomDate && (
                    <div className={clsx(["flex", "w-full", "pl-12", "ml-1"])}>
                      <TextInput
                        placeholder={t("transaction_page.date_placeholder")}
                        value={date}
                        onChange={(e) => setDate(e?.target?.value)}
                        label={t("Fecha")}
                        type="date"
                        required
                        disabled={isLoading}
                      />
                    </div>
                  )}
                </div>
                <div className={clsx(["flex", "w-full", "flex-col", "gap-1"])}>
                  <CheckBox
                    disabled={isLoading}
                    label="Incluir los archivos adjuntos"
                    onChange={(isChecked) => setKeepAttachments(isChecked)}
                    checked={keepAttachments}
                    description={
                      "Duplicar todos los datos incluyendo los archivos adjuntos"
                    }
                  />
                  {keepAttachments && (
                    <div
                      className={clsx([
                        "flex",
                        "w-full",
                        "pl-12",
                        "ml-1",
                        "warning-text",
                      ])}
                    >
                      Dependiendo del tamaño y la cantidad de archivos el
                      proceso puede tardar un poco más.
                    </div>
                  )}
                </div>
                <div className={clsx(["flex", "w-full", "flex-col", "gap-1"])}>
                  <CheckBox
                    disabled={isLoading}
                    label="Duplicar a otra cuenta"
                    onChange={(isChecked) => setIsUsingCustomAccount(isChecked)}
                    checked={isUsingCustomAccount}
                    description={t(
                      "dialogs.duplicate_transactions.custom_account_text",
                      {
                        count,
                      }
                    )}
                  />
                  {isUsingCustomAccount && (
                    <div className={clsx(["flex", "w-full", "pl-12", "ml-1"])}>
                      <CustomSelect
                        disabled={isLoading}
                        label="Cuenta destino"
                        options={
                          transformToSelectOptions({
                            data: allUserAccounts?.filter?.(
                              (account: any) =>
                                account?.account !== mainFilter?.account
                            ),
                            labelField: "name",
                            valueField: "account",
                          }) as Array<ICustomSelectOption>
                        }
                        value={account}
                        useValueAsLabel={false}
                        onChange={(e: any) => setAccount(e?.value)}
                        required
                        placeholder="Selecciona una cuenta"
                      />
                    </div>
                  )}
                </div>
                <div className="warning-text mt-2">
                  La configuración de recurrencia no se incluye en las
                  transacciones duplicadas.
                </div>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            text: t("Duplicar"),
            onClick: () => {
              if (fieldsAreCompleted) {
                // setIsRunning(!isRunning);
                onConfirm({
                  date: {
                    change: isUsingCustomDate,
                    value: date,
                  },
                  account: {
                    change: isUsingCustomAccount,
                    value: account,
                  },
                  includeAttachments: keepAttachments,
                });
              }
            },
            icon: <MdCheck size={ICON_SIZES.MD} />,
            disabled: isLoading || !fieldsAreCompleted,
            isLoading,
            kind: EButtonKind.SAVE,
          },
          {
            text: "Cancelar",
            onClick: () => {
              if (!isLoading) {
                onClose();
              }
            },
            icon: <MdClose size={ICON_SIZES.MD} />,
            kind: EButtonKind.NORMAL,
            disabled: isLoading,
          },
        ]}
      />
    </Modal>
  );
};

export default DuplicateTransactionsDialog;
